import { useState, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteImages } from '@/mocks/images';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleContact = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9spcddlbrt1qcgaj7ig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.meta?.detail || responseText || '提交失败，请重试';
        setFormStatus('error');
        setFormError(msg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于心灵家缘',
    description: '心灵家缘是一个专注于家庭情感关系研究的平台，帮助每个家庭成员找到恰当的角色定位',
    url: 'https://xinlingjiayuan.com/about',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '心灵家缘是一个什么样的平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '心灵家缘是一个专注于家庭情感关系研究的平台，围绕「年轻的小姨子」等家庭角色，深入探讨家庭情感边界的建立与维护，提供实用的家庭相处技巧与情感沟通建议。',
        },
      },
      {
        '@type': 'Question',
        name: '如何建立健康的家庭情感边界？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '建立健康的家庭情感边界需要遵循五个关键原则：尊重每个人的感受、保持边界清晰、持续一致地维护、在特殊情况下灵活调整、确保边界对所有人公平。',
        },
      },
      {
        '@type': 'Question',
        name: '姐夫与小姨子之间如何保持适当距离？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '建议以"家人+朋友"的模式相处，通过姐姐作为沟通纽带，避免单独长时间相处，在言行举止中注意分寸，始终将姐姐的感受放在首位。',
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([aboutSchema, faqSchema]) }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-14 min-h-screen bg-background-50">
        {/* About Hero */}
        <section className="relative w-full h-56 md:h-80 rounded-2xl overflow-hidden mb-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className="absolute inset-0 mx-4 md:mx-6 rounded-2xl overflow-hidden">
            <img
              src={siteImages.aboutHero}
              alt="关于心灵家缘"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-black/20"></div>
          </div>
          <div className="relative z-10 h-full flex items-center justify-center mx-4 md:mx-6">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-white">关于我们</h1>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]} />

          <div className="flex flex-col lg:flex-row gap-10">
            {/* Main Content */}
            <div className="flex-1">
              {/* Brand Introduction */}
              <section className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    <strong>心灵家缘</strong>是一个专注于<strong>家庭情感关系</strong>研究的综合性内容平台。
                    我们围绕<strong>「年轻的小姨子」</strong>这一独特的家庭角色，深入探讨家庭关系中
                    <strong>情感边界</strong>的建立、<strong>家庭角色定位</strong>的明确以及
                    <strong>和谐相处技巧</strong>的应用。
                  </p>
                  <p>
                    在现代家庭结构中，<strong>年轻的小姨子</strong>与姐夫之间的关系一直是一个敏感而微妙的话题。
                    我们的使命是帮助每个家庭成员找到恰当的位置，在保持亲情温暖的同时，建立健康的情感边界，
                    让每一个家庭都能享受到和谐美满的关系。
                  </p>
                  <p>
                    我们的编辑团队由资深的家庭关系咨询师、心理学专家和情感专栏作家组成。
                    每一篇文章都经过深入的研究和精心的编写，确保内容的专业性和实用性。
                    我们相信，好的家庭关系不是天生的，而是可以通过学习和实践来培养的。
                  </p>
                </div>
              </section>

              {/* Our Values */}
              <section className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                  我们的理念
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-5 bg-background-100 rounded-xl border border-background-200/60">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mb-3">
                      <i className="ri-heart-line text-primary-600 text-lg"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">以爱为本</h3>
                    <p className="text-sm text-foreground-500">所有建议都建立在爱与尊重的基础上，不制造对立，只传递温暖。</p>
                  </div>
                  <div className="p-5 bg-background-100 rounded-xl border border-background-200/60">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mb-3">
                      <i className="ri-shield-check-line text-accent-600 text-lg"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">专业可靠</h3>
                    <p className="text-sm text-foreground-500">内容由专业人士把关，确保每一条建议都有理论支撑和实操价值。</p>
                  </div>
                  <div className="p-5 bg-background-100 rounded-xl border border-background-200/60">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 mb-3">
                      <i className="ri-lightbulb-line text-secondary-600 text-lg"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">实用导向</h3>
                    <p className="text-sm text-foreground-500">关注实际可操作的技巧和方法，让读者学得会、用得上。</p>
                  </div>
                </div>
              </section>

              {/* Reputation */}
              <section className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                  读者口碑
                </h2>
                <div className="space-y-4">
                  <div className="p-5 bg-background-100 rounded-xl border border-background-200/60">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-amber-400 text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 mb-3 leading-relaxed">
                      "作为一个年轻的已婚女性，这个网站帮我理清了很多家庭关系中的困惑。
                      特别是关于<strong>小姨子相处之道</strong>的文章，让我明白了如何更好地引导妹妹与丈夫之间的互动。
                      强烈推荐给每个有类似困扰的家庭成员。"
                    </p>
                    <p className="text-xs text-foreground-400">— 李女士，32岁，北京</p>
                  </div>
                  <div className="p-5 bg-background-100 rounded-xl border border-background-200/60">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-amber-400 text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 mb-3 leading-relaxed">
                      "内容非常专业且实用。关于<strong>家庭情感边界</strong>的几篇文章让我对家庭关系有了全新的认识。
                      现在我在处理亲戚关系时更加从容了。感谢心灵家缘团队的努力。"
                    </p>
                    <p className="text-xs text-foreground-400">— 张先生，38岁，上海</p>
                  </div>
                  <div className="p-5 bg-background-100 rounded-xl border border-background-200/60">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-amber-400 text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 mb-3 leading-relaxed">
                      "我是家里的姐姐，一直在为<strong>年轻的小姨子</strong>和丈夫之间的关系感到焦虑。
                      这个网站给了我很多启发和实用的建议，让我学会了如何用智慧而不是紧张来处理这个问题。"
                    </p>
                    <p className="text-xs text-foreground-400">— 王女士，29岁，广州</p>
                  </div>
                </div>
              </section>

              {/* FAQ */}
              <section className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                  常见问题
                </h2>
                <div className="space-y-3">
                  <details className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                    <summary className="p-5 font-medium text-foreground-900 cursor-pointer hover:text-primary-600 transition-colors flex items-center justify-between">
                      心灵家缘是一个什么样的平台？
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                      心灵家缘是一个专注于家庭情感关系研究的平台，围绕「年轻的小姨子」等家庭角色，
                      深入探讨家庭情感边界的建立与维护，提供实用的家庭相处技巧与情感沟通建议。
                    </div>
                  </details>
                  <details className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                    <summary className="p-5 font-medium text-foreground-900 cursor-pointer hover:text-primary-600 transition-colors flex items-center justify-between">
                      网站的内容是否专业可靠？
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                      我们的编辑团队由资深的家庭关系咨询师和心理学专家组成。每一篇文章都经过深入研究和精心编写，
                      确保内容的专业性和实用性。
                    </div>
                  </details>
                  <details className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                    <summary className="p-5 font-medium text-foreground-900 cursor-pointer hover:text-primary-600 transition-colors flex items-center justify-between">
                      如何与心灵家缘取得联系？
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                      您可以通过本页下方的联系表单，或发送邮件至 contact@xinlingjiayuan.com 与我们取得联系。
                      我们会在1-2个工作日内回复您的消息。
                    </div>
                  </details>
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <aside className="w-full lg:w-80 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                {/* Contact Form */}
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                    联系我们
                  </h3>
                  <p className="text-sm text-foreground-500 mb-4">
                    有任何问题或建议？欢迎给我们留言
                  </p>

                  {formStatus === 'success' ? (
                    <div className="text-sm text-secondary-700 bg-secondary-100 rounded-lg px-4 py-4">
                      感谢您的留言！我们会尽快回复。
                    </div>
                  ) : (
                    <form ref={formRef} onSubmit={handleContact} className="space-y-3" data-readdy-form="">
                      <input
                        type="text"
                        name="company_alt"
                        className="form-honeypot"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      <div>
                        <input
                          type="text"
                          name="name"
                          placeholder="您的姓名"
                          required
                          className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 bg-background-50 text-foreground-900 placeholder:text-foreground-400"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          placeholder="您的邮箱"
                          required
                          className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 bg-background-50 text-foreground-900 placeholder:text-foreground-400"
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          placeholder="请输入您的留言"
                          rows={4}
                          maxLength={500}
                          required
                          className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 bg-background-50 text-foreground-900 placeholder:text-foreground-400 resize-none"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                      </div>
                      <button
                        type="submit"
                        className="w-full px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                      >
                        发送留言
                      </button>
                      {formStatus === 'error' && (
                        <p className="text-xs text-red-600">{formError}</p>
                      )}
                    </form>
                  )}
                </div>

                {/* Contact Info */}
                <div className="mt-5 bg-background-100 rounded-xl p-5 border border-background-200/60">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                    联系方式
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">邮箱</p>
                        <p className="text-sm text-foreground-700">contact@xinlingjiayuan.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">地址</p>
                        <p className="text-sm text-foreground-700">北京市朝阳区建国路88号</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-time-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">工作时间</p>
                        <p className="text-sm text-foreground-700">周一至周五 9:00 - 18:00</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}